import { CommitStrategy, useScribe } from "@elevenlabs/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "@/lib/api-service";

interface UseElevenLabsSTTOptions {
	campaignSlug: string | undefined;
}

/**
 * ElevenLabs Scribe realtime STT hook.
 *
 * Wraps `useScribe` from @elevenlabs/react and exposes the same surface as
 * the old `useSpeechRecognition` hook so it can be a drop-in replacement in
 * `useSequentialInterview`.
 *
 * Uses VAD commit strategy so committed transcripts arrive automatically when
 * the user pauses speaking.
 */
export function useElevenLabsSTT({ campaignSlug }: UseElevenLabsSTTOptions) {
	const [isActive, setIsActive] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const connectingRef = useRef(false);
	const wasConnectedRef = useRef(false);

	const {
		connect,
		disconnect,
		isConnected,
		partialTranscript,
		committedTranscripts,
		clearTranscripts,
	} = useScribe({
		commitStrategy: CommitStrategy.VAD,
		languageCode: "en",
		modelId: "scribe_v2_realtime",
		microphone: {
			echoCancellation: true,
			noiseSuppression: true,
			autoGainControl: true,
		},
	});

	// Build the full committed text from segments
	const transcript = committedTranscripts
		.filter((s) => s.isFinal)
		.map((s) => s.text)
		.join(" ");

	const interimTranscript = partialTranscript;

	// Track connection state — only reset isActive when a previously
	// established connection drops, not during the initial connect phase.
	useEffect(() => {
		if (isConnected) {
			wasConnectedRef.current = true;
			connectingRef.current = false;
		} else if (wasConnectedRef.current && isActive) {
			// Connection dropped after being established
			wasConnectedRef.current = false;
			setIsActive(false);
		}
	}, [isConnected, isActive]);

	const startListening = useCallback(async () => {
		if (!campaignSlug || isActive || connectingRef.current) return;

		connectingRef.current = true;
		setIsActive(true);
		setError(null);
		try {
			const { token } = await apiService.getSequentialSTTToken(campaignSlug);
			await connect({ token });
		} catch (err) {
			const msg =
				err instanceof DOMException && err.name === "NotAllowedError"
					? "Microphone permission denied"
					: err instanceof Error
						? err.message
						: "Failed to start recording";
			console.error("[ElevenLabs STT] Failed to start:", err);
			setError(msg);
			setIsActive(false);
			connectingRef.current = false;
		}
	}, [campaignSlug, connect, isActive]);

	const stopListening = useCallback(() => {
		// Disconnect stops the microphone but committed transcripts remain
		// available in committedTranscripts. The consumer should read
		// transcript (derived from committedTranscripts) before calling
		// resetTranscript to avoid losing pending partial text.
		disconnect();
		setIsActive(false);
		connectingRef.current = false;
		wasConnectedRef.current = false;
	}, [disconnect]);

	const resetTranscript = useCallback(() => {
		clearTranscripts();
	}, [clearTranscripts]);

	return {
		isSupported: true as const,
		isListening: isActive,
		transcript,
		interimTranscript,
		startListening,
		stopListening,
		resetTranscript,
		error,
	};
}
